/**
 * ChatTransferKeyAgreement.ts
 *
 * X25519 ECDH + HKDF-SHA256 transfer-key derivation.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatTransferPairing.swift` →
 * `ChatTransferKDF.deriveTransferKey`. Same HKDF info string
 * `kaether:chat-transfer:key:v1`, zero salt, 32-byte output. Both endpoints
 * compute the same `transferKey` because X25519 ECDH is symmetric.
 *
 * ## Native WebCrypto path
 *
 * Modern Chrome/Edge/Safari/Firefox expose X25519 directly:
 *     crypto.subtle.generateKey({ name: 'X25519' }, ...)
 *     crypto.subtle.deriveBits({ name: 'X25519', public }, priv, 256)
 *
 * That is the default code path here.
 *
 * ## Dependency-injection seam
 *
 * Some test/headless contexts may need to substitute the X25519 op (e.g. a
 * Node test harness without native WebCrypto X25519, or a polyfill). The
 * top-level `deriveTransferKey` accepts an optional `ops` parameter that
 * carries pluggable `x25519Ecdh` and `hkdf` callbacks. When omitted, the
 * SubtleCrypto-backed defaults are used. The self-test exercises the default
 * path to make sure native WebCrypto works; the DI seam exists so a future
 * Node test runner can substitute libsodium without rewriting this file.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Pluggable crypto operations. All callbacks must be byte-faithful with the
 * underlying SubtleCrypto primitives — same input bytes in, same output bytes
 * out, so substituting an implementation cannot change the derived key.
 */
export interface ChatTransferCryptoOps {
  /**
   * X25519 ECDH. Inputs: 32-byte raw private scalar, 32-byte raw public key.
   * Output: 32-byte shared secret.
   */
  readonly x25519Ecdh: (
    privateKeyScalar: Uint8Array,
    publicKeyBytes: Uint8Array,
  ) => Promise<Uint8Array>;
  /**
   * HKDF-SHA256 with caller-supplied IKM, salt, and info. Output length is
   * caller-controlled. For the transfer key, `lengthBytes` is 32.
   */
  readonly hkdf: (
    ikm: Uint8Array,
    salt: Uint8Array,
    info: Uint8Array,
    lengthBytes: number,
  ) => Promise<Uint8Array>;
}

/** Raw X25519 key pair: 32-byte private scalar + 32-byte public key. */
export interface X25519KeyPair {
  readonly privateKey: Uint8Array;
  readonly publicKey: Uint8Array;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** HKDF info for the AEAD key. Bound to v1 of the transfer protocol. */
export const HKDF_INFO_TRANSFER_KEY = new TextEncoder().encode(
  "kaether:chat-transfer:key:v1",
);

/** HKDF salt for the transfer key (all-zero, 32 bytes — matches iOS). */
export const HKDF_SALT_ZERO_32 = new Uint8Array(32);

/** Output width of the transfer key. */
export const TRANSFER_KEY_BYTES = 32;

// ---------------------------------------------------------------------------
// Native SubtleCrypto implementation (default ops)
// ---------------------------------------------------------------------------

const PKCS8_X25519_HEADER = new Uint8Array([
  0x30, 0x2e, // SEQUENCE, length 46
  0x02, 0x01, 0x00, // INTEGER version = 0
  0x30, 0x05, // SEQUENCE (algorithm identifier)
  0x06, 0x03, 0x2b, 0x65, 0x6e, // OID 1.3.101.110 (id-X25519)
  0x04, 0x22, // OCTET STRING, length 34
  0x04, 0x20, // OCTET STRING, length 32 (the actual key)
]);

async function nativeX25519Ecdh(
  privateKeyScalar: Uint8Array,
  publicKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  if (privateKeyScalar.length !== 32) {
    throw new Error(
      `ChatTransferKeyAgreement: private scalar must be 32 bytes, got ${privateKeyScalar.length}`,
    );
  }
  if (publicKeyBytes.length !== 32) {
    throw new Error(
      `ChatTransferKeyAgreement: public key must be 32 bytes, got ${publicKeyBytes.length}`,
    );
  }
  // Wrap the raw scalar in PKCS#8 DER so SubtleCrypto will accept it. The
  // header is fixed for X25519 — only the 32-byte scalar tail varies.
  const pkcs8 = new Uint8Array(PKCS8_X25519_HEADER.length + privateKeyScalar.length);
  pkcs8.set(PKCS8_X25519_HEADER, 0);
  pkcs8.set(privateKeyScalar, PKCS8_X25519_HEADER.length);
  const privKey = await crypto.subtle.importKey(
    "pkcs8",
    pkcs8,
    { name: "X25519" },
    false,
    ["deriveBits"],
  );
  const pubKey = await crypto.subtle.importKey(
    "raw",
    publicKeyBytes,
    { name: "X25519" },
    false,
    [],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "X25519", public: pubKey },
    privKey,
    256,
  );
  return new Uint8Array(bits);
}

async function nativeHkdf(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  lengthBytes: number,
): Promise<Uint8Array> {
  const ikmKey = await crypto.subtle.importKey(
    "raw",
    ikm,
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt,
      info,
    },
    ikmKey,
    lengthBytes * 8,
  );
  return new Uint8Array(bits);
}

/** Singleton of the default native-WebCrypto ops. */
export const DEFAULT_OPS: ChatTransferCryptoOps = {
  x25519Ecdh: nativeX25519Ecdh,
  hkdf: nativeHkdf,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate a fresh ephemeral X25519 key pair using native WebCrypto.
 *
 * Returned bytes are raw — 32-byte private scalar, 32-byte public key.
 * The caller is responsible for scoping the private scalar tightly so it
 * goes out of scope as soon as the session terminates. JavaScript offers no
 * deterministic memory wipe; tight scoping is the only mitigation.
 */
export async function generateEphemeralKeyPair(): Promise<X25519KeyPair> {
  // X25519 is an asymmetric algorithm, so SubtleCrypto returns a CryptoKeyPair
  // here — but the type union includes the symmetric `CryptoKey` branch, so
  // we narrow explicitly. The cast is safe because the algorithm we requested
  // (`X25519`) is asymmetric by construction.
  const kp = (await crypto.subtle.generateKey(
    { name: "X25519" },
    true,
    ["deriveBits"],
  )) as CryptoKeyPair;
  const pubRaw = new Uint8Array(await crypto.subtle.exportKey("raw", kp.publicKey));
  const privPkcs8 = new Uint8Array(await crypto.subtle.exportKey("pkcs8", kp.privateKey));
  // PKCS#8 wrapper is exactly 16 bytes (matches PKCS8_X25519_HEADER); the
  // 32-byte scalar follows. Slice it out.
  const privKey = privPkcs8.slice(16, 48);
  return { privateKey: privKey, publicKey: pubRaw };
}

/**
 * Derive the 32-byte transfer key from one side's ephemeral private scalar
 * and the peer's ephemeral public key.
 *
 * Both endpoints call this with their respective inputs and arrive at the
 * same key — that is the symmetry property X25519 ECDH gives us. From there
 * the AEAD key, the bundle nonces, and the user-visible verification code
 * are all derived deterministically from the same bytes.
 *
 * @param ownEphemeralPriv  Local 32-byte X25519 private scalar.
 * @param peerEphemeralPub  Peer's 32-byte X25519 public key.
 * @param ops               Optional DI seam. Omit for native SubtleCrypto.
 */
export async function deriveTransferKey(
  ownEphemeralPriv: Uint8Array,
  peerEphemeralPub: Uint8Array,
  ops: ChatTransferCryptoOps = DEFAULT_OPS,
): Promise<Uint8Array> {
  const shared = await ops.x25519Ecdh(ownEphemeralPriv, peerEphemeralPub);
  return ops.hkdf(
    shared,
    HKDF_SALT_ZERO_32,
    HKDF_INFO_TRANSFER_KEY,
    TRANSFER_KEY_BYTES,
  );
}
